<template>
  <div class="space-y-5">
    <!-- Table Controls -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <!-- Search -->
      <div class="relative flex-1 max-w-md group">
        <MagnifyingGlassIcon class="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-surface-400 group-focus-within:text-accent-500 transition-colors" />
        <input
          v-model="searchValue"
          type="text"
          :placeholder="`Search ${searchPlaceholder}...`"
          class="input pl-11 w-full !rounded-xl"
        />
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-2">
        <slot name="actions"></slot>

        <BaseButton
          v-if="showExport"
          variant="ghost"
          size="sm"
          @click="exportData"
          :icon="ArrowDownTrayIcon"
        >
          Export
        </BaseButton>

        <div v-if="showPagination" class="flex items-center gap-2">
          <span class="text-xs font-medium text-surface-500">Show:</span>
          <select
            v-model="rowsPerPageValue"
            class="input py-1.5 text-sm min-w-[72px] pr-8 !rounded-lg"
            @change="handleRowsPerPageChange"
          >
            <option v-for="option in rowsPerPageOptions" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-hidden rounded-xl border border-surface-200/80 dark:border-surface-800/80">
      <Vue3EasyDataTable
        :key="`table-${rowsPerPageValue}-${searchValue}`"
        :headers="headers"
        :items="items"
        :search-value="searchValue"
        :rows-per-page="rowsPerPageValue"
        :table-class-name="tableClassName"
        :header-text-direction="headerTextDirection"
        :body-text-direction="bodyTextDirection"
        :loading="loading"
        :border-cell="borderCell"
        :alternating="alternating"
        :theme-color="themeColor"
        :rows-items="rowsPerPageOptions"
        hide-rows-per-page
        @click-row="handleRowClick"
      >
        <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
          <slot :name="slot" v-bind="scope" />
        </template>

        <template #loading>
          <div class="flex items-center justify-center py-16">
            <LoadingSpinner message="Loading data..." />
          </div>
        </template>
      </Vue3EasyDataTable>
    </div>

    <!-- Pagination Info -->
    <div v-if="showPagination && items.length > 0" class="flex items-center justify-between text-xs text-surface-500 dark:text-surface-500">
      <div class="font-medium">
        {{ items.length }} entries
        <span v-if="searchValue" class="text-accent-600 dark:text-accent-400">(filtered)</span>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && items.length === 0" class="text-center py-16">
      <slot name="empty">
        <component v-if="emptyIcon" :is="emptyIcon" class="h-12 w-12 mx-auto text-surface-300 dark:text-surface-700 mb-4" />
        <p class="text-surface-600 dark:text-surface-400 text-base font-semibold mb-1">
          {{ emptyTitle }}
        </p>
        <p class="text-surface-400 dark:text-surface-600 text-sm">
          {{ emptyMessage }}
        </p>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import BaseButton from './BaseButton.vue'
import LoadingSpinner from './LoadingSpinner.vue'
import { MagnifyingGlassIcon, ArrowDownTrayIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  headers: { type: Array, required: true },
  items: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: 'Search' },
  showExport: { type: Boolean, default: true },
  showPagination: { type: Boolean, default: true },
  rowsPerPage: { type: Number, default: 10 },
  rowsPerPageOptions: { type: Array, default: () => [10, 25, 50, 100] },
  tableClassName: { type: String, default: 'enhanced-table' },
  headerTextDirection: { type: String, default: 'left' },
  bodyTextDirection: { type: String, default: 'left' },
  borderCell: { type: Boolean, default: false },
  alternating: { type: Boolean, default: true },
  themeColor: { type: String, default: '#10B981' },
  emptyTitle: { type: String, default: 'No data available' },
  emptyMessage: { type: String, default: 'Try adjusting your filters or search query' },
  emptyIcon: { type: Object, default: null },
  exportFilename: { type: String, default: 'export.csv' }
})

const emit = defineEmits(['row-click', 'export'])

const searchValue = ref('')
const rowsPerPageValue = ref(props.rowsPerPage)

watch(() => props.rowsPerPage, (newVal) => {
  rowsPerPageValue.value = newVal
})

const handleRowClick = (item) => {
  emit('row-click', item)
}

const handleRowsPerPageChange = () => {}

const exportData = () => {
  if (props.items.length === 0) return

  const headers = props.headers.map(h => h.text).join(',')
  const rows = props.items.map(item => {
    return props.headers.map(h => {
      const value = item[h.value]
      return typeof value === 'string' && value.includes(',') ? `"${value}"` : value
    }).join(',')
  })

  const csv = [headers, ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', props.exportFilename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  emit('export', csv)
}
</script>
